import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertTriangle, ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * Public account-deletion page — /delete-account.
 *
 * Registered as the Google Play Console external Account Deletion URL.
 * Must work WITHOUT app install and WITHOUT existing login.
 *
 * ── Feb 2026 rebuild — Verify-first two-step flow ──
 *
 * PASSWORD:
 *   choose → (fill creds) → confirm → (type phrase) → delete
 *
 * GOOGLE:
 *   choose → Google OAuth (select_account prompt) → return here →
 *   backend verifies code, returns { email, delete_ticket } →
 *   confirm (shows verified email prominently) → (type phrase) →
 *   delete with ticket
 *
 * The type-to-confirm phrase-match is deliberately forgiving:
 *   • all whitespace stripped (leading, trailing, mid, NBSP, tabs)
 *   • case-insensitive
 *   • unicode-normalised
 *   • onChange + onInput both wire the value (defends against iOS/Safari
 *     autofill silently mutating the input without firing React onChange)
 */

const CONFIRM_PHRASE = "DELETE MY ACCOUNT";
const CONFIRM_PHRASE_NORMALIZED = "DELETEMYACCOUNT"; // whitespace-stripped

// Bulletproof phrase normalizer used for both the enable-button check and
// the value we send to the backend. Handles unicode zero-widths, NBSPs,
// tabs, and any leading/trailing weirdness.
function _normalizePhrase(raw) {
  if (typeof raw !== "string") return "";
  return raw
    .normalize("NFKC")
    .replace(/\s+/g, "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width chars
    .toUpperCase();
}

// What's deleted vs retained — sourced from the Privacy Policy so the two
// surfaces stay in sync. Displayed prominently for Play Store compliance.
const DELETED_ITEMS = [
  "Your profile (name, email, username, bio, profile picture)",
  "All study data — sessions, streak, To-Do list, syllabus (subjects & chapters), mock test results, achievements",
  "Community activity — posts, comments, friend connections, direct messages",
  "Syllox AI sessions and message history",
  "Payment history and Razorpay order references",
  "Push notification subscriptions and device tokens",
  "Authentication artifacts (password hash, active session tokens, pending password-reset tokens)",
];

const RETAINED_ITEMS = [
  {
    label: "Anonymised deletion audit log",
    detail: "A single row noting that a deletion occurred at this timestamp. Your email is stored masked (e.g. \"ab***@example.com\") and your phone (if any) shows only the last 4 digits. Retained for GDPR-style compliance audit and trimmed after ~50,000 records.",
  },
  {
    label: "Aggregate, anonymous analytics",
    detail: "Google Analytics 4 metrics (visits, page views, referrers) are pre-aggregated and never tied to your user ID. They cannot be used to identify you after deletion.",
  },
];

const LS = {
  intent: "syllox_oauth_intent",   // set to "delete" before Google redirect
};

function _clearOauthFlags() {
  try { localStorage.removeItem(LS.intent); } catch { /* noop */ }
}

export default function DeleteAccountPage() {
  useEffect(() => {
    document.title = "Delete your Syllox account · Syllox";
  }, []);

  // Stage state machine:
  //   "choose"   — Step 1, pick Email+Password or Google, collect creds
  //   "verifying"— Google roundtrip in progress
  //   "confirm"  — Step 2, verified identity shown, waiting for phrase + click
  //   "success"  — post-delete success card
  const [stage, setStage] = useState("choose");
  // Verified identity payload — persists across stages.
  //   Password: { method: "password", email, password }
  //   Google:   { method: "google",   email, delete_ticket }
  const [verified, setVerified] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Google-return interceptor: if we come back from Google OAuth (?code=…)
  // with our delete intent set, verify the code and jump to Step 2.
  const consumedCodeRef = useRef(false);
  useEffect(() => {
    if (consumedCodeRef.current) return;
    let intent = null;
    try { intent = localStorage.getItem(LS.intent); } catch { /* noop */ }
    if (intent !== "delete") return;

    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const oauthErr = url.searchParams.get("error");
    if (!code && !oauthErr) return;

    consumedCodeRef.current = true;
    // Scrub URL immediately so a refresh doesn't re-consume a spent code.
    window.history.replaceState({}, "", url.pathname);
    _clearOauthFlags();

    if (oauthErr) {
      setErrorMsg("Google verification was cancelled. Please try again.");
      setStage("choose");
      return;
    }

    setStage("verifying");
    setErrorMsg(null);
    // Must match the redirect_uri Google saw during the kickoff.
    // GoogleCallback.jsx forwards the code here with the delete intent.
    const redirect_uri = window.location.origin + "/auth/google/callback";
    api.post("/account/public-verify-google-for-delete", { code, redirect_uri })
      .then((resp) => {
        const email = resp?.data?.email;
        const delete_ticket = resp?.data?.delete_ticket;
        if (!email || !delete_ticket) throw new Error("Bad verify response");
        setVerified({ method: "google", email, delete_ticket });
        setStage("confirm");
      })
      .catch((err) => {
        const msg = formatApiError(err?.response?.data?.detail) || "Google verification failed. Please try again.";
        setErrorMsg(msg);
        setStage("choose");
      });
  }, []);

  return (
    <PublicLayout
      title="Delete your Syllox account · Syllox"
      description="Request permanent deletion of your Syllox account and personal data. Verify with your Syllox password or Google sign-in — no app installation required."
    >
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8" data-testid="delete-account-root">
        {/* Hero — Play Store's reviewer needs to see this at a glance. */}
        <div className="flex flex-col items-start gap-4 rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-900/40 dark:bg-red-950/20 sm:flex-row sm:items-center sm:p-6" data-testid="delete-hero">
          <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-500 text-white">
            <ShieldAlert size={22} />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold leading-tight sm:text-3xl" data-testid="delete-heading">
              Delete your Syllox account
            </h1>
            <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">
              Permanently and immediately remove your account and personal study data. This action cannot be undone.
            </p>
          </div>
        </div>

        {/* Compliance section — always visible so Play Store reviewers can
            read what's deleted and retained WITHOUT interacting. */}
        <section className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="delete-transparency">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
            <h2 className="font-heading text-base font-semibold text-red-700 dark:text-red-400">What is deleted</h2>
            <ul className="mt-3 space-y-1.5 text-sm text-zinc-700 dark:text-zinc-300" data-testid="delete-list-deleted">
              {DELETED_ITEMS.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
            <h2 className="font-heading text-base font-semibold text-emerald-700 dark:text-emerald-400">What is retained (and why)</h2>
            <ul className="mt-3 space-y-3 text-sm text-zinc-700 dark:text-zinc-300" data-testid="delete-list-retained">
              {RETAINED_ITEMS.map((r) => (
                <li key={r.label}>
                  <div className="font-medium">{r.label}</div>
                  <div className="text-xs text-zinc-600 dark:text-zinc-400">{r.detail}</div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] text-zinc-500 dark:text-zinc-500">
              See our <Link to="/privacy" className="underline hover:text-emerald-600">Privacy Policy</Link> for full details.
            </p>
          </div>
        </section>

        {/* Actual deletion flow */}
        <section className="mt-8" data-testid="delete-form-section">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900 sm:p-6">
            {stage === "choose" && (
              <ChooseStep
                errorMsg={errorMsg}
                onError={setErrorMsg}
                onPasswordVerified={(v) => { setErrorMsg(null); setVerified(v); setStage("confirm"); }}
              />
            )}
            {stage === "verifying" && <VerifyingScreen />}
            {stage === "confirm" && verified && (
              <ConfirmStep
                verified={verified}
                onBack={() => { setStage("choose"); setVerified(null); setErrorMsg(null); }}
                onSuccess={() => { setStage("success"); setErrorMsg(null); }}
                onError={setErrorMsg}
                errorMsg={errorMsg}
              />
            )}
            {stage === "success" && <SuccessStep />}
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300" data-testid="delete-inapp-hint">
          <div className="font-medium">Already logged in to the Syllox app?</div>
          <div className="mt-0.5 text-xs">
            You can also delete your account from the app: <span className="whitespace-nowrap font-medium">Settings → Delete Account</span>. Both flows have identical effect.
          </div>
        </section>

        <section className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-500">
          Need help? Email <a href="mailto:contact@syllox.in" className="font-medium text-emerald-600 hover:underline">contact@syllox.in</a>.
        </section>
      </div>
    </PublicLayout>
  );
}

/* ─────────────────────────── Step 1: Choose method ─────────────────────────── */

function ChooseStep({ errorMsg, onError, onPasswordVerified }) {
  const [tab, setTab] = useState("password");
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-700 dark:text-red-400" data-testid="step-label">
        Step 1 of 2 · Verify your identity
      </div>
      <h2 className="mt-1 font-heading text-xl font-semibold">How did you sign up?</h2>
      <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
        Choose the same method you used to create your Syllox account.
      </p>

      <div className="mt-5">
        <TabBar tab={tab} onChange={(t) => { setTab(t); onError(null); }} />

        <div className="mt-5">
          {tab === "password"
            ? <PasswordVerifyForm onVerified={onPasswordVerified} onError={onError} />
            : <GoogleKickoffForm onError={onError} />}

          {errorMsg && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-300 bg-red-100 p-3 text-sm text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200" data-testid="delete-error-banner">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>{errorMsg}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabBar({ tab, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-zinc-200 bg-zinc-100 p-1 dark:border-zinc-800 dark:bg-zinc-900" data-testid="delete-tab-bar">
      <button
        type="button"
        onClick={() => onChange("password")}
        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
          tab === "password"
            ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-white"
            : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        }`}
        data-testid="delete-tab-password"
      >
        Email + Password
      </button>
      <button
        type="button"
        onClick={() => onChange("google")}
        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
          tab === "google"
            ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-white"
            : "text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        }`}
        data-testid="delete-tab-google"
      >
        Google Sign-In
      </button>
    </div>
  );
}

function PasswordVerifyForm({ onVerified, onError }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const disabled = !email.trim() || !password;
  const submit = (e) => {
    e.preventDefault();
    if (disabled) return;
    onError(null);
    onVerified({ method: "password", email: email.trim(), password });
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="delete-password-form">
      <div className="space-y-1.5">
        <Label htmlFor="delete-email">Email</Label>
        <Input
          id="delete-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="delete-email-input"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="delete-password">Password</Label>
        <div className="relative">
          <Input
            id="delete-password"
            type={showPw ? "text" : "password"}
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pr-10"
            data-testid="delete-password-input"
          />
          <button
            type="button"
            onClick={() => setShowPw((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            aria-label={showPw ? "Hide password" : "Show password"}
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>
      <Button
        type="submit"
        disabled={disabled}
        className="w-full h-11 bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
        data-testid="delete-password-continue-btn"
      >
        Continue →
      </Button>
      <p className="text-[11px] text-zinc-500">
        Nothing is deleted yet. You will confirm in the next step.
      </p>
    </form>
  );
}

function GoogleKickoffForm({ onError }) {
  const clickGoogle = () => {
    onError(null);
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      onError("Google Sign-In is not configured. Please email contact@syllox.in to delete your account.");
      return;
    }
    try { localStorage.setItem(LS.intent, "delete"); } catch { /* noop */ }
    // Route through the already-whitelisted /auth/google/callback so we don't
    // have to add /delete-account to the Google Cloud Console redirect URIs.
    // GoogleCallback.jsx recognises intent==="delete" and forwards the code
    // back to /delete-account?code=… where our verify effect picks it up.
    const redirect = window.location.origin + "/auth/google/callback";
    const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
    url.searchParams.set("client_id", clientId);
    url.searchParams.set("redirect_uri", redirect);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("scope", "openid email profile");
    url.searchParams.set("prompt", "select_account");
    url.searchParams.set("access_type", "online");
    window.location.href = url.toString();
  };

  return (
    <div className="space-y-3" data-testid="delete-google-form">
      <p className="text-sm text-zinc-600 dark:text-zinc-400">
        We will send you to Google to pick the account you want deleted. After you verify, we will bring you back here to type the confirmation phrase.
      </p>
      <Button
        type="button"
        onClick={clickGoogle}
        className="w-full h-11 bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
        data-testid="delete-google-continue-btn"
      >
        Continue with Google →
      </Button>
      <p className="text-[11px] text-zinc-500">
        Nothing is deleted yet. Verification happens on Google, then you confirm here.
      </p>
    </div>
  );
}

/* ─────────────────────────── Verifying (Google roundtrip) ─────────────────────────── */

function VerifyingScreen() {
  return (
    <div className="flex flex-col items-center py-10 text-center" data-testid="delete-verifying">
      <Loader2 size={28} className="animate-spin text-emerald-600" />
      <p className="mt-3 text-sm font-medium">Verifying with Google…</p>
      <p className="mt-1 text-xs text-zinc-500">Please wait, this takes a moment.</p>
    </div>
  );
}

/* ─────────────────────────── Step 2: Confirm ─────────────────────────── */

function ConfirmStep({ verified, onBack, onSuccess, onError, errorMsg }) {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Bulletproof phrase match — whitespace-stripped, case-insensitive,
  // unicode-normalised, zero-width chars removed.
  const normalized = useMemo(() => _normalizePhrase(confirmText), [confirmText]);
  const confirmValid = normalized === CONFIRM_PHRASE_NORMALIZED;

  // Defensive: sync from the DOM after mount in case autofill mutated the
  // input without dispatching an onChange event (iOS Safari edge case).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const t = setTimeout(() => {
      if (el.value && el.value !== confirmText) setConfirmText(el.value);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clickConfirm = useCallback(async () => {
    if (submitting) return;
    // Re-read from DOM at click time — belt-and-braces against iOS autofill.
    const currentDomValue = inputRef.current?.value ?? confirmText;
    if (currentDomValue !== confirmText) setConfirmText(currentDomValue);
    const currentNormalized = _normalizePhrase(currentDomValue);
    if (currentNormalized !== CONFIRM_PHRASE_NORMALIZED) {
      onError(`Please type "${CONFIRM_PHRASE}" exactly to confirm.`);
      return;
    }
    onError(null);
    setSubmitting(true);
    try {
      if (verified.method === "password") {
        await api.post("/account/public-delete-with-password", {
          email: verified.email,
          password: verified.password,
          confirm_text: CONFIRM_PHRASE,
        });
      } else {
        // Google — we already have a verified delete_ticket from Step 1.
        await api.post("/account/public-delete-with-ticket", {
          delete_ticket: verified.delete_ticket,
          confirm_text: CONFIRM_PHRASE,
        });
      }
      onSuccess();
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Deletion failed. Please try again.";
      onError(msg);
      setSubmitting(false);
    }
  }, [submitting, confirmText, verified, onSuccess, onError]);

  const isGoogle = verified.method === "google";

  return (
    <div data-testid="delete-confirm-step">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-xs font-semibold text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        data-testid="delete-back-btn"
      >
        <ArrowLeft size={12} /> Back
      </button>
      <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-red-700 dark:text-red-400" data-testid="step-label">
        Step 2 of 2 · Final confirmation
      </div>
      <h2 className="mt-1 font-heading text-xl font-semibold">This will permanently delete your Syllox account.</h2>

      {/* Verified identity card — prominent, always visible */}
      <div
        className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30"
        data-testid="delete-identity-card"
      >
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
            {isGoogle ? "Verified with Google" : "Account to delete"}
          </div>
          <div className="mt-0.5 break-all text-sm font-semibold text-zinc-900 dark:text-zinc-100" data-testid="delete-identity-label">
            {verified.email}
          </div>
          {!isGoogle && (
            <div className="mt-0.5 text-[11px] text-zinc-600 dark:text-zinc-400">
              Your password will be verified with this request.
            </div>
          )}
        </div>
      </div>

      {/* The prominent, unmissable type-to-confirm gate. Rendered ABOVE the
          delete button on every viewport — this was the original bug. */}
      <div className="mt-5 rounded-xl border border-red-200 bg-red-50/60 p-4 dark:border-red-900/40 dark:bg-red-950/20">
        <Label htmlFor="delete-confirm-input" className="text-red-900 dark:text-red-200">
          Type <span className="font-mono font-bold">{CONFIRM_PHRASE}</span> to confirm
        </Label>
        <div className="relative">
          <Input
            id="delete-confirm-input"
            ref={inputRef}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onInput={(e) => setConfirmText(e.currentTarget.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            autoFocus
            inputMode="text"
            className={`mt-2 border-red-300 bg-white pr-10 font-mono uppercase focus-visible:ring-red-500 dark:border-red-900/60 dark:bg-red-950/10 ${
              confirmValid ? "border-emerald-500 focus-visible:ring-emerald-500" : ""
            }`}
            placeholder={CONFIRM_PHRASE}
            data-testid="delete-confirm-input"
          />
          {confirmValid && (
            <CheckCircle2
              size={16}
              className="absolute right-3 top-1/2 mt-1 -translate-y-1/2 text-emerald-600"
              data-testid="delete-confirm-match-icon"
              aria-label="Phrase matches"
            />
          )}
        </div>
        <p className={`mt-2 text-xs ${confirmValid ? "text-emerald-700 dark:text-emerald-400" : "text-red-800/80 dark:text-red-300/70"}`} data-testid="delete-confirm-hint">
          {confirmValid
            ? "Phrase matched. The delete button below is now active."
            : "Case- and whitespace-tolerant. The delete button unlocks when the phrase matches."}
        </p>
      </div>

      {errorMsg && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-red-300 bg-red-100 p-3 text-sm text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200" data-testid="delete-error-banner">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>{errorMsg}</div>
        </div>
      )}

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={submitting}
          className="w-full sm:w-auto"
          data-testid="delete-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={clickConfirm}
          disabled={!confirmValid || submitting}
          className="w-full sm:w-auto sm:min-w-[280px] h-12 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="delete-final-confirm-btn"
        >
          {submitting
            ? <><Loader2 size={16} className="animate-spin" /> <span className="ml-2">Deleting…</span></>
            : "Confirm Delete My Account"}
        </Button>
      </div>
    </div>
  );
}

/* ─────────────────────────── Success ─────────────────────────── */

function SuccessStep() {
  useEffect(() => {
    try {
      localStorage.removeItem("syllox_access_token");
      _clearOauthFlags();
    } catch { /* noop */ }
  }, []);

  return (
    <div className="flex flex-col items-center rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center dark:border-emerald-900/40 dark:bg-emerald-950/20" data-testid="delete-success-card">
      <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
        <CheckCircle2 size={22} />
      </div>
      <h3 className="font-heading text-xl font-bold" data-testid="delete-success-heading">
        Account deleted
      </h3>
      <p className="mt-2 max-w-md text-sm text-zinc-700 dark:text-zinc-300">
        Your Syllox account and personal study data have been permanently removed. See our{" "}
        <Link to="/privacy" className="underline hover:text-emerald-600">Privacy Policy</Link>{" "}for what is retained (and why).
      </p>
      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Link to="/">
          <Button variant="outline" data-testid="delete-success-home-btn">Return home</Button>
        </Link>
      </div>
    </div>
  );
}
